import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  ArrowRight, Film, Calendar, Users, Video, 
  BookOpen, Briefcase, Heart, Play, ChevronRight, MapPin, Clock, Newspaper
} from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple inline link converter with markdown support
const LinkedText = ({ text }) => {
  if (!text) return null;
  
  // Combined regex: markdown links [text](url) OR raw URLs
  const combinedRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s<\[\]]+[^<.,:;"')\]\s])/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    if (match[1] && match[2]) {
      // Markdown link: [text](url)
      parts.push(
        <a
          key={match.index}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-purple-light hover:text-white transition-colors underline underline-offset-2"
        >
          {match[1]}
        </a>
      );
    } else if (match[3]) {
      // Raw URL
      const url = match[3];
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-purple-light hover:text-white transition-colors underline underline-offset-2"
        >
          {url.length > 40 ? url.slice(0, 40) + '...' : url}
        </a>
      );
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  if (parts.length === 0) {
    return <>{text}</>;
  }
  
  return <>{parts}</>;
};

// Parse date string as local date (not UTC) to prevent timezone shift
const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
};

// Check if event is in the past
const isPastEvent = (event) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = parseLocalDate(event.date);
  eventDate.setHours(23, 59, 59, 999);
  return eventDate < today;
};

export default function Home() {
  const [news, setNews] = useState([]);
  const [films, setFilms] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [newsRes, filmsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/news?limit=3`),
        axios.get(`${API}/films?limit=4`),
        axios.get(`${API}/events?limit=10`), // Fetch more to filter
      ]);
      // /api/news returns { items, ... } once paginated; a bare array before that.
      setNews(Array.isArray(newsRes.data) ? newsRes.data : (newsRes.data.items || []));
      setFilms(filmsRes.data);
      // Filter out past events and take only upcoming ones
      const upcomingEvents = eventsRes.data
        .filter(e => !isPastEvent(e))
        .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date))
        .slice(0, 4);
      setEvents(upcomingEvents);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Film, title: "Films", desc: "Curated showcase of AI-generated cinema", link: "/films" },
    { icon: Newspaper, title: "News", desc: "Latest from the AI film world", link: "/news" },
    { icon: Calendar, title: "Festivals", desc: "Global festival calendar & coverage", link: "/festivals" },
    { icon: BookOpen, title: "Resources", desc: "Tools, guides & learning materials", link: "/resources" },
    { icon: MapPin, title: "Events", desc: "Screenings, meetups & happenings", link: "/events" },
    { icon: Video, title: "Workshops", desc: "Learn AI filmmaking techniques", link: "/workshops" },
    { icon: Users, title: "Membership", desc: "Join the global AI film community", link: "/join" },
    { icon: Heart, title: "About", desc: "Our mission and story", link: "/about" },
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Home"
        description="AI Film Society - The global community for AI filmmakers. Discover films, connect with directors, attend events and workshops."
        url="https://aifilmsociety.org"
      />
      
      {/* SEO-friendly intro content for crawlers */}
      <article className="sr-only">
        <h1>AI Film Society - Global Community for AI Filmmakers</h1>
        <p>
          Welcome to AI Film Society, the premier global community for AI filmmakers, enthusiasts, and industry pioneers. 
          We connect creators exploring artificial intelligence in cinema through festivals, workshops, film showcases, and networking events.
        </p>
        <h2>What We Offer</h2>
        <ul>
          <li>AI Film Festivals - Global calendar of AI cinema festivals and submission deadlines</li>
          <li>Film Showcase - Curated collection of AI-generated films and experimental cinema</li>
          <li>Director Profiles - Meet visionary filmmakers pushing boundaries with AI</li>
          <li>Workshops - Learn AI filmmaking techniques from industry experts</li>
          <li>Community Events - Screenings, meetups, and networking opportunities</li>
          <li>Resources - Tools, guides and learning materials for AI filmmakers</li>
          <li>Job Board - Career opportunities in the AI film industry</li>
          <li>Support Us - Help grow and sustain the AI film community</li>
        </ul>
        <h2>Join the AI Filmmaking Revolution</h2>
        <p>
          Whether you're a seasoned filmmaker exploring AI tools or a technology enthusiast interested in creative applications, 
          AI Film Society provides resources, connections, and opportunities to advance your craft.
        </p>
      </article>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-glow" aria-label="Hero">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1758718917089-fb30fd6d06c0?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="AI filmmaking abstract visualization"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-void/50 via-void/80 to-void" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-purple mb-6 font-mono">
              The Future of Filmmaking
            </p>
            <h1 className="font-syne text-5xl sm:text-6xl lg:text-8xl font-bold text-white leading-[0.9] tracking-tighter mb-8">
              Where AI Meets<br />
              <span className="gradient-text">Cinema</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mb-12 font-light leading-relaxed">
              The global community for AI filmmakers, enthusiasts, and industry pioneers. 
              Explore festivals, discover directors, and shape the future of storytelling.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/join"
                data-testid="hero-join-btn"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-bold uppercase tracking-widest text-xs rounded-full hover:bg-slate-200 transition-all duration-300"
              >
                Join the Society
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/films"
                data-testid="hero-explore-btn"
                className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white font-bold uppercase tracking-widest text-xs rounded-full hover:border-white/40 transition-all duration-300"
              >
                Explore Films
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" aria-label="Explore AI Film Society">
        <div className="max-w-7xl mx-auto">
          <h2 className="sr-only">Explore AI Film Society</h2>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={feature.link}
                  data-testid={`feature-${feature.title.toLowerCase().replace(' ', '-')}`}
                  className="block h-full p-6 bg-void-surface border border-white/5 hover:border-purple/20 transition-all duration-500 group card-hover"
                >
                  <feature.icon className="w-7 h-7 text-slate-500 mb-4 group-hover:text-purple-light transition-colors" aria-hidden="true" />
                  <h3 className="font-syne font-bold text-white text-base mb-1">{feature.title}</h3>
                  <p className="text-xs text-slate-500">{feature.desc}</p>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Latest News */}
      {news.length > 0 && (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-void-surface">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-purple mb-2 font-mono">Latest</p>
                <h2 className="font-syne text-3xl md:text-4xl font-bold text-white">AI Film News</h2>
              </div>
              <Link 
                to="/news" 
                data-testid="news-view-all"
                className="text-sm text-slate-500 hover:text-white flex items-center gap-1 transition-colors"
              >
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {news.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <Link to={`/news/${article.id}`} data-testid={`news-card-${index}`}>
                    <div className="aspect-video mb-4 overflow-hidden bg-void">
                      <img 
                        src={article.image_url || "https://images.unsplash.com/photo-1705412893210-b15bcac44d33?crop=entropy&cs=srgb&fm=jpg&q=85"}
                        alt={article.title}
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
                      />
                    </div>
                    <p className="text-xs uppercase tracking-wider text-purple mb-2 font-mono">{article.category}</p>
                    <h3 className="font-syne font-bold text-white text-lg mb-2 group-hover:text-slate-300 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-slate-500 line-clamp-2"><LinkedText text={article.excerpt} /></p>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events */}
      {events.length > 0 && (
        <section className="py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-purple mb-2 font-mono">Calendar</p>
                <h2 className="font-syne text-3xl md:text-4xl font-bold text-white">Upcoming Events</h2>
              </div>
              <Link 
                to="/events" 
                data-testid="events-view-all"
                className="text-sm text-slate-500 hover:text-white flex items-center gap-1 transition-colors"
              >
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link 
                    to="/events"
                    data-testid={`event-card-${index}`}
                    className="flex items-start gap-4 p-5 bg-void-surface border border-white/5 hover:border-purple/20 transition-all duration-500 group"
                  >
                    <div className="flex-shrink-0 text-center">
                      <p className="text-2xl font-syne font-bold text-white">
                        {parseLocalDate(event.date).getDate()}
                      </p>
                      <p className="text-xs uppercase tracking-wider text-slate-500">
                        {parseLocalDate(event.date).toLocaleDateString('en-US', { month: 'short' })}
                      </p>
                    </div>
                    <div className="flex-grow min-w-0">
                      <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-wider mb-2 ${
                        event.event_type === 'meetup' 
                          ? 'bg-purple/20 text-purple-light' 
                          : 'bg-white/5 text-slate-400'
                      }`}>
                        {event.event_type}
                      </span>
                      <h3 className="font-syne font-bold text-white mb-1 group-hover:text-slate-300 transition-colors truncate">
                        {event.title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {event.time}
                        </span>
                        <span className="flex items-center gap-1 truncate">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Films */}
      {films.length > 0 && (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-void-highlight">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-purple mb-2 font-mono">Showcase</p>
                <h2 className="font-syne text-3xl md:text-4xl font-bold text-white">AI Film Spotlight</h2>
              </div>
              <Link 
                to="/films" 
                data-testid="films-view-all"
                className="text-sm text-slate-500 hover:text-white flex items-center gap-1 transition-colors"
              >
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {films.map((film, index) => (
                <motion.div
                  key={film.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <Link to={`/films/${film.id}`} data-testid={`film-card-${index}`}>
                    <div className="aspect-video mb-4 overflow-hidden bg-void-surface relative">
                      <img 
                        src={film.poster_url || "https://images.unsplash.com/photo-1696479670605-aeeb206e1fcb?crop=entropy&cs=srgb&fm=jpg&q=85"}
                        alt={film.title}
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                          <Play className="w-5 h-5 text-white ml-1" />
                        </div>
                      </div>
                      {film.festival_winner && (
                        <div className="absolute top-3 left-3 px-2 py-1 bg-purple/80 text-white text-xs uppercase tracking-wider font-mono">
                          Winner
                        </div>
                      )}
                    </div>
                    <h3 className="font-syne font-bold text-white mb-1 group-hover:text-slate-300 transition-colors">
                      {film.title}
                    </h3>
                    <p className="text-sm text-slate-500">{film.director} • {film.year}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-void-surface">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-syne text-4xl md:text-5xl font-bold text-white mb-6">
              Join the AI Film Revolution
            </h2>
            <p className="text-lg text-slate-400 mb-12 max-w-2xl mx-auto">
              Connect with filmmakers, access exclusive resources, and be part of the community 
              shaping the future of cinema.
            </p>
            <Link
              to="/join"
              data-testid="cta-join-btn"
              className="inline-flex items-center gap-2 px-10 py-5 bg-white text-black font-bold uppercase tracking-widest text-sm rounded-full hover:bg-slate-200 transition-all duration-300"
              onClick={() => window.scrollTo(0, 0)}
            >
              Become a Member
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
